import { PlusCircle } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FormField,
  FormInput,
  FormSelect,
} from '@/_components';

import { FormLayout } from '../../../layout';
import { cityNameToFormValue, countryNameToFormValue } from '../shared';
import type { AddressFieldsProps } from '../types';

export function AddressFields({
  form,
  isEdit,
  cityOptions,
  stateOptions,
  countriesOptions,
  isLookingUpPostalCode = false,
  showCityAsInput = false,
  showCountryAsInput = false,
  showRegion = false,
}: AddressFieldsProps) {
  const [address2] = form.watch(['address2']);

  return (
    <FormLayout>
      <Accordion
        type="single"
        collapsible
        disabled={!isEdit || Boolean(address2)}
        className="col-span-12 w-full"
        value={address2 ? 'item-1' : undefined}
      >
        <AccordionItem value="item-1">
          <section className="flex flex-1 items-start justify-between">
            <FormField
              control={form.control}
              name="address1"
              render={({ field }) => (
                <FormInput
                  {...field}
                  disabled={!isEdit}
                  label="Address 1"
                  formItemClassName={twMerge('flex-1', !isEdit && 'mr-0')}
                  labelProps={{ className: 'w-[calc(100%_+_1.75rem)]' }}
                />
              )}
            />
            <div
              className={`flex h-11 w-7 items-center justify-center gap-x-1 border-b ${!isEdit ? 'border-c-border-muted' : 'border-c-border'} pt-2 transition-all delay-300`}
            >
              {!isEdit ? (
                <AccordionTrigger hideIcon={!isEdit} icon={PlusCircle} className="size-4 shrink-0 grow-0" />
              ) : (
                <AccordionTrigger hideIcon={!isEdit} icon={PlusCircle} className="size-4 shrink-0 grow-0" />
              )}
            </div>
          </section>
          <AccordionContent>
            <FormField
              control={form.control}
              name="address2"
              render={({ field }) => (
                <FormInput {...field} disabled={!isEdit} label="Address 2" formItemClassName="mt-8" />
              )}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <FormField
        control={form.control}
        name="postal_code"
        render={({ field }) => (
          <FormInput
            formItemClassName="xs:col-span-6 col-span-12 2xl:col-span-2"
            {...field}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
            disabled={!isEdit}
            label="Postal Code"
          />
        )}
      />
      <section className="xs:col-span-6 col-span-12 2xl:col-span-3">
        <FormField
          control={form.control}
          name="city"
          render={({ field: { ref: _, ...field } }) =>
            showCityAsInput ? (
              <FormInput
                {...field}
                value={field.value?.name ?? ''}
                disabled={!isEdit}
                label="City"
                onChange={(e) => field.onChange(cityNameToFormValue(e.target.value))}
              />
            ) : (
              <FormSelect
                {...field}
                disabled={!isEdit}
                label="City"
                options={cityOptions}
                getOptionLabel={(option) => {
                  if (option?.name === '') {
                    return 'None';
                  }
                  return option.name;
                }}
                isLoading={isLookingUpPostalCode}
              />
            )
          }
        />
      </section>
      <section className="xs:col-span-6 col-span-12 2xl:col-span-3">
        {showRegion && (
          <FormField
            control={form.control}
            name="region"
            render={({ field }) => <FormInput {...field} disabled={!isEdit} label="Region" />}
          />
        )}
        {!showRegion && (
          <FormField
            control={form.control}
            name="state"
            render={({ field: { ref: _, ...field } }) => (
              <FormSelect
                {...field}
                disabled={!isEdit}
                label="State"
                options={stateOptions}
                getOptionLabel={(option) => {
                  if (!option || option?.name === '') {
                    return 'None';
                  }
                  return option?.abbreviation || option.name;
                }}
                getOptionValue={(option) => option?.name || ''}
                isLoading={isLookingUpPostalCode}
              />
            )}
          />
        )}
      </section>
      <section className="xs:col-span-6 col-span-12 2xl:col-span-4">
        <FormField
          control={form.control}
          name="country"
          render={({ field: { ref: _, ...field } }) =>
            showCountryAsInput ? (
              <FormInput
                {...field}
                value={field.value?.name ?? ''}
                disabled={!isEdit}
                label="Country (required)"
                onChange={(e) => field.onChange(countryNameToFormValue(e.target.value))}
              />
            ) : (
              <FormSelect
                {...field}
                options={countriesOptions}
                disabled={!isEdit}
                label="Country (required)"
                getOptionValue={(option) => option?.name || ''}
                getOptionLabel={(option) => option.name}
                isLoading={isLookingUpPostalCode}
              />
            )
          }
        />
      </section>
    </FormLayout>
  );
}
